export type BadgeTone = 'amber' | 'neutral' | 'blue'

export interface BadgeLabel {
  key: string
  params?: Record<string, string>
}

export type BadgeState =
  | { kind: 'new-draft'; label: BadgeLabel; tone: 'amber' }
  | { kind: 'draft-of-existing'; label: BadgeLabel; tone: 'amber' }
  | { kind: 'view-latest'; label: BadgeLabel; tone: 'neutral' }
  | { kind: 'view-historical'; label: BadgeLabel; tone: 'blue' }

export interface BadgeStateInput {
  blockId: string | undefined
  isDraft: boolean
  currentVersion: number
  /**
   * `undefined`  = versions list not yet loaded (spinner still in flight).
   *               Badge shows "Draft of v?" — genuinely unknown.
   * `0`          = versions list loaded successfully but empty (brand-new
   *               block — first save, never published yet).
   *               Badge shows "Draft of v1" — we know it's the first version.
   * `number > 0` = highest published version number.
   */
  latestPublishedVersion: number | undefined
  /**
   * True once `listBlockVersions` has returned (successfully or with 0 items).
   * Distinguishes "loaded, 0 versions" from "not loaded yet" so the badge
   * can show "Draft of v1" instead of "Draft of v?" after the first save of
   * a new block.
   */
  versionsLoaded?: boolean
}

export function deriveBadgeState(input: BadgeStateInput): BadgeState {
  const { blockId, isDraft, currentVersion, latestPublishedVersion, versionsLoaded } = input

  if (!blockId || blockId === 'new') {
    return {
      kind: 'new-draft',
      label: { key: 'blockConfigurator.badge.draftOf', params: { version: '1' } },
      tone: 'amber',
    }
  }

  if (isDraft) {
    let next: string
    if (!versionsLoaded) {
      // Versions list not yet loaded — genuinely unknown.
      next = '?'
    } else if (latestPublishedVersion === undefined || latestPublishedVersion === 0) {
      // Loaded and empty (or explicit 0) → first-ever publish will be v1.
      next = '1'
    } else {
      next = String(latestPublishedVersion + 1)
    }
    return {
      kind: 'draft-of-existing',
      label: { key: 'blockConfigurator.badge.draftOf', params: { version: next } },
      tone: 'amber',
    }
  }

  if (latestPublishedVersion !== undefined && currentVersion < latestPublishedVersion) {
    return {
      kind: 'view-historical',
      label: {
        key: 'blockConfigurator.badge.viewingHistorical',
        params: { version: String(currentVersion) },
      },
      tone: 'blue',
    }
  }

  return {
    kind: 'view-latest',
    label: {
      key: 'blockConfigurator.badge.viewingLatest',
      params: { version: String(currentVersion) },
    },
    tone: 'neutral',
  }
}
